import React, { useEffect, useState } from "react";
import {
	Box,
	TextField,
	Typography,
	List,
	ListItem,
	ListItemText,
	Skeleton,
	Tooltip,
	IconButton,
	Badge,
} from "@mui/material";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FileCopyIcon from "@mui/icons-material/FileCopy";

import { useAppDispatch, useAppSelector } from "../../../store/hooks.ts";
import {
	searchSchedules,
	selectScheduleSearchResults,
	setSearchQuery,
} from "../../../store/slices/schedulesSearchSlice.ts";

const ItemNumbers = () => {
	const dispatch = useAppDispatch();
	const [searchTerm, setSearchTerm] = useState("");
	const [debouncedTerm, setDebouncedTerm] = useState("");

	const [copiedItemNum, setCopiedItemNum] = useState<string | null>(null);

	const results = useAppSelector(selectScheduleSearchResults);
	const loading = useAppSelector((state) => state.schedulesSearch.loading);

	// Debounce input by 400ms
	useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedTerm(searchTerm);
		}, 400);
		return () => clearTimeout(handler);
	}, [searchTerm]);

	// Trigger search when debounced input updates
	useEffect(() => {
		if (debouncedTerm.trim() === "") return;
		dispatch(setSearchQuery(debouncedTerm));
		dispatch(searchSchedules(debouncedTerm));
	}, [debouncedTerm, dispatch]);

	return (
		<Box sx={{ maxWidth: 500, mt: 2 }}>
			<TextField
				fullWidth
				variant="outlined"
				size="small"
				placeholder="Search Item Numbers"
				value={searchTerm}
				onChange={(e) => setSearchTerm(e.target.value)}
			/>

			{searchTerm.trim() === "" && (
				<Typography
					variant="body2"
					color="text.secondary"
					sx={{ mt: 1, ml: 0.5, textAlign: "center" }}
				>
					Search by item number or item description
				</Typography>
			)}

			{loading ? (
				<List dense sx={{ mt: 2 }}>
					{Array.from({ length: 4 }).map((_, i) => (
						<ListItem key={i}>
							<ListItemText
								primary={
									<Skeleton variant="text" width="80%" />
								}
								secondary={
									<Skeleton variant="text" width="60%" />
								}
							/>
						</ListItem>
					))}
				</List>
			) : (
				searchTerm.trim() !== "" && (
					<List dense sx={{ mt: 2 }}>
						{results.map((result) => {
							const handleCopyClick = async () => {
								try {
									await navigator.clipboard.writeText(
										result.item_num
									);

									setCopiedItemNum(result.item_num);
									setTimeout(
										() => setCopiedItemNum(null),
										1000
									);
								} catch (err) {
									console.error(
										"Clipboard write failed:",
										err
									);
								}
							};

							const isCopied = copiedItemNum === result.item_num;

							return (
								<ListItem
									key={result.item_num}
									secondaryAction={
										<Box sx={{ display: "flex", gap: 1 }}>
											<Tooltip title={result.description}>
												<IconButton size="small">
													<InfoOutlinedIcon fontSize="small" />
												</IconButton>
											</Tooltip>
											<Badge
												color="success"
												badgeContent="Copied!"
												invisible={!isCopied}
												anchorOrigin={{
													vertical: "top",
													horizontal: "right",
												}}
											>
												<IconButton
													size="small"
													onClick={handleCopyClick}
												>
													{isCopied ? (
														<FileCopyIcon fontSize="small" />
													) : (
														<ContentCopyIcon fontSize="small" />
													)}
												</IconButton>
											</Badge>
										</Box>
									}
								>
									<ListItemText
										primary={
											<Typography fontWeight="bold">
												{result.item_num}
											</Typography>
										}
									/>
								</ListItem>
							);
						})}
					</List>
				)
			)}
		</Box>
	);
};

export default ItemNumbers;
